import moment from "moment";
import "moment/locale/es";
import { HeaderEmpresaCard } from "../Components/PdfComponents/HeaderEmpresaCard";
import BIWO_LOGO_FOOTER from "../Components/PdfComponents/componentsv2.0/biwoLogoFooter";

moment.locale("es");

const BLANK_PNG =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";

const esImagenPdfSoportada = (s) => typeof s === "string" && /^data:image\/(png|jpe?g);base64,/i.test(s);

// Mismo layout punteado de los demás reportes (ventas, compras).
const layoutPunteado = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => "#adaaaa",
  vLineColor: () => "#adaaaa",
  hLineStyle: (i, node) => (i === 0 || i === node.table.body.length ? null : { dash: { length: 2, space: 1 } }),
  vLineStyle: (i, node) => (i === 0 || i === node.table.widths.length ? null : { dash: { length: 2 } }),
};

const layoutTabla = {
  ...layoutPunteado,
  fillColor: (rowIndex) => (rowIndex % 2 === 0 && rowIndex !== 0 ? "#e8e8e8" : "#ffffff"),
};

const cabecera = (titulos) =>
  titulos.map((t) =>
    typeof t === "string"
      ? { text: t, style: "tituloTabla" }
      : { text: t.text, style: "tituloTabla", alignment: t.align || "center" },
  );

const celda = (texto, align = "left") => ({ text: texto, style: "textoTabla", alignment: align });

/**
 * Fila de totales con el mismo aspecto que la de los reportes de ventas y
 * compras: gris, letra chica y el rótulo en la primera columna.
 */
const filaTotal = (etiqueta, valores, numColumnas) => {
  const fila = [
    { text: etiqueta, style: "totalChico", alignment: "left", fillColor: "#cccccc" },
    ...valores.map((v) => ({ text: String(v), style: "totalChico", alignment: "right", fillColor: "#cccccc" })),
  ];
  while (fila.length < numColumnas) {
    fila.splice(1, 0, { text: "", style: "totalChico", fillColor: "#cccccc" });
  }
  return fila;
};

const seccion = (texto) => ({ text: texto, style: "subheader", margin: [0, 6, 0, 2] });

const tabla = (widths, body) => ({
  style: "tableExample",
  table: { widths, headerRows: 1, body },
  layout: layoutTabla,
});

/**
 * Reporte de Soporte Técnico del corporativo.
 * @param {*} values.empresa Empresa completa (para el encabezado con logo)
 * @param {*} values.corporativo { nombre, empresas: [{ nombre }] }
 * @param {*} values.fechaInicial / values.fechaFinal Periodo consultado (moment o Date, opcionales)
 * @param {*} values.alcance Texto de cuántas empresas reportaron
 * @param {*} values.resumen Arreglo de pares [indicador, valor]
 * @param {*} values.porFase / values.porEmpresa / values.porReportero Datos de las tablas
 */
const reporteSoportePdf = (values) => {
  const empresa = values.empresa || {};
  const corporativo = values.corporativo || {};
  const resumen = values.resumen || [];
  const porFase = values.porFase || [];
  const porEmpresa = values.porEmpresa || [];
  const porReportero = values.porReportero || [];
  const variasEmpresas = porEmpresa.length > 1;

  const hayPeriodo = !!(values.fechaInicial && values.fechaFinal);

  const terceraColumna = {
    border: [false, false, false, false],
    table: {
      headerRows: 1,
      widths: ["100%"],
      body: [
        ...(hayPeriodo
          ? [
              [{ text: "Fecha Inicial:", style: "subheader", alignment: "center" }],
              [
                {
                  text: moment(values.fechaInicial).locale("es").format("LL"),
                  style: "textoChico",
                  alignment: "center",
                },
              ],
              [{ text: "Fecha Final:", style: "subheader", alignment: "center" }],
              [
                {
                  text: moment(values.fechaFinal).locale("es").format("LL"),
                  style: "textoChico",
                  alignment: "center",
                },
              ],
            ]
          : [
              [{ text: "Periodo:", style: "subheader", alignment: "center" }],
              [{ text: "Histórico completo", style: "textoChico", alignment: "center" }],
            ]),
        [{ text: "Fecha de Impresión:", style: "subheader", alignment: "center" }],
        [{ text: moment().locale("es").format("LL"), style: "textoChico", alignment: "center" }],
      ],
    },
    layout: "noBorders",
  };

  // --- Datos de filtrado ---
  const filtrados = [];
  if (corporativo.nombre) {
    filtrados.push([
      { text: "Corporativo:", style: "subheader" },
      { text: corporativo.nombre, style: "textoChico" },
    ]);
  }
  filtrados.push([
    { text: "Periodo:", style: "subheader" },
    {
      text: hayPeriodo
        ? `${moment(values.fechaInicial).locale("es").format("LL")} — ${moment(values.fechaFinal)
            .locale("es")
            .format("LL")}`
        : "Histórico completo",
      style: "textoChico",
    },
  ]);
  if (values.alcance) {
    filtrados.push([
      { text: "Alcance:", style: "subheader" },
      { text: values.alcance, style: "textoChico" },
    ]);
  }
  if (corporativo.empresas && corporativo.empresas.length > 0) {
    filtrados.push([
      { text: "Empresas:", style: "subheader" },
      { text: corporativo.empresas.map((e) => e.nombre).join("\n"), style: "textoChico" },
    ]);
  }

  // --- Tablas del reporte ---
  const totalPorFase = porFase.reduce((suma, f) => suma + (f.total || 0), 0);
  const tablaPorFase = [
    seccion("Reportes por estado"),
    tabla(
      ["70%", "30%"],
      [
        cabecera(["Estado", "Reportes"]),
        ...porFase.map((f) => [celda(f.label), celda(String(f.total), "right")]),
        filaTotal("TOTAL:", [totalPorFase], 2),
      ],
    ),
  ];

  const tablaPorEmpresa = variasEmpresas
    ? [
        seccion("Reportes por empresa"),
        tabla(
          ["40%", "15%", "15%", "15%", "15%"],
          [
            cabecera([
              "Empresa",
              { text: "Reportes", align: "center" },
              { text: "Abiertos", align: "center" },
              { text: "Atendidos", align: "center" },
              { text: "Rechazados", align: "center" },
            ]),
            ...porEmpresa.map((e) => [
              celda(e.nombre),
              celda(String(e.total), "right"),
              celda(String(e.abiertos), "right"),
              celda(String(e.atendidos), "right"),
              celda(String(e.rechazados), "right"),
            ]),
            filaTotal(
              "TOTAL:",
              [
                porEmpresa.reduce((s, e) => s + (e.total || 0), 0),
                porEmpresa.reduce((s, e) => s + (e.abiertos || 0), 0),
                porEmpresa.reduce((s, e) => s + (e.atendidos || 0), 0),
                porEmpresa.reduce((s, e) => s + (e.rechazados || 0), 0),
              ],
              5,
            ),
          ],
        ),
      ]
    : [];

  const tablaPorReportero = [
    seccion("Quién reportó"),
    tabla(
      variasEmpresas ? ["30%", "30%", "25%", "15%"] : ["40%", "45%", "15%"],
      [
        cabecera([
          "Usuario",
          "Correo",
          ...(variasEmpresas ? ["Empresa"] : []),
          { text: "Reportes", align: "center" },
        ]),
        ...porReportero.map((r) => [
          celda(r.nombre),
          celda(r.correo || "—"),
          ...(variasEmpresas ? [celda(r.empresa || "—")] : []),
          celda(String(r.total), "right"),
        ]),
        filaTotal("TOTAL:", [porReportero.reduce((s, r) => s + (r.total || 0), 0)], variasEmpresas ? 4 : 3),
      ],
    ),
  ];

  const dd = {
    footer: {
      image: "logoBiwo",
      alignment: "center",
      width: 80,
    },
    content: [
      {
        style: "tableExample",
        table: {
          widths: ["18%", "52%", "30%"],
          layout: { defaultBorder: false },
          body: [[...HeaderEmpresaCard(empresa, false, false, terceraColumna)]],
        },
      },
      {
        text: "Reporte de Soporte Técnico",
        style: "tableHeaderIzquierda",
      },
      { text: "Datos de filtrado:" },
      {
        style: "tableExample",
        table: {
          widths: ["20%", "80%"],
          layout: { defaultBorder: false },
          body: filtrados,
        },
        layout: layoutPunteado,
      },

      seccion("Resumen"),
      tabla(
        ["60%", "40%"],
        [
          cabecera(["Indicador", "Valor"]),
          ...resumen.map(([indicador, valor]) => [celda(indicador), celda(valor, "right")]),
        ],
      ),

      ...tablaPorFase,
      ...tablaPorEmpresa,
      ...tablaPorReportero,
    ],
    images: {
      logo: esImagenPdfSoportada(empresa?.logo?.base64Completa) ? empresa.logo.base64Completa : BLANK_PNG,
      logoBiwo: BIWO_LOGO_FOOTER,
    },
    styles: {
      tableHeaderIzquierda: { bold: true, fontSize: 18, color: "black" },
      header: {
        fontSize: 9,
        bold: true,
      },
      subheader: {
        fontSize: 8,
        bold: true,
      },
      tableExample: {
        margin: [0, 5, 0, 5],
      },
      tablaSinMargen: {
        margin: [0, 0, 0, 0],
      },
      tableHeader: {
        bold: true,
        fontSize: 12,
        color: "black",
        alignment: "center",
      },
      textoChico: {
        fontSize: 8,
      },
      tituloTabla: {
        fontSize: 8,
        bold: true,
        fillColor: "#cccccc",
        style: "subheader",
        alignment: "center",
        border: true,
      },
      textoTabla: {
        fontSize: 8,
        italics: false,
      },
      totalChico: {
        fontSize: 6,
        bold: true,
        fillColor: "#cccccc",
        alignment: "right",
        border: true,
      },
    },
    pageSize: "LETTER",
    pageMargins: [20, 20, 20, 60],
  };

  return dd;
};

export default reporteSoportePdf;
